import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import siteHtml from './landing198x/site.html?raw';
import siteCss from './landing198x/styles.css?raw';
import { mountPricing } from './landing198x/pricing';

// The 198X marketing home page. The visual design is ported verbatim from the
// approved prototype (HTML + CSS injected as-is for fidelity); only the wiring is
// React: nav links smooth-scroll, "Sign in" → /login, and "Get started" / "Solve
// my sales" / "Start with this plan" open the existing contact form (POST /api/v1/contact).
export default function LandingPage() {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const [showContact, setShowContact] = useState(false);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    // Build the interactive pricing configurator into the injected #priceconfig/#cartcol.
    mountPricing(() => setShowContact(true));

    // Delegate clicks for nav links / CTAs (the prototype used data-go="…").
    const onClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('[data-go], .brandlogo');
      if (!target) return;
      e.preventDefault();
      const go = target.getAttribute('data-go');
      if (target.classList.contains('brandlogo') || !go) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (go === 'signin') navigate('/login');
      else if (go === 'register') setShowContact(true);
      else {
        // how / model / discovery / pricing → scroll to the section
        document.getElementById(go)?.scrollIntoView({ behavior: 'smooth' });
      }
    };
    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [navigate]);

  return (
    <div className="x198" ref={rootRef}>
      <style>{siteCss}</style>
      <div dangerouslySetInnerHTML={{ __html: siteHtml }} />
      <ContactModal open={showContact} onClose={() => setShowContact(false)} />
    </div>
  );
}

function ContactModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form, setForm] = useState({ name: '', work_email: '', company: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  if (!open) return null;

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      const res = await fetch('/api/v1/contact', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Could not submit — please try again');
      setDone(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const inp = 'w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 outline-none transition';

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        {done ? (
          <div className="text-center py-6">
            <div className="text-3xl mb-3">🎉</div>
            <h3 className="text-lg font-bold text-gray-900">Thanks, {form.name.split(' ')[0] || 'there'}!</h3>
            <p className="text-sm text-gray-500 mt-2">Our team will reach out shortly to solve your sales, end to end.</p>
            <button onClick={onClose} className="mt-5 px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Solve my sales</h3>
              <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">✕</button>
            </div>
            <p className="text-sm text-gray-500 -mt-1 mb-1">Tell us about you and we'll be in touch.</p>
            <input required placeholder="Full name *" value={form.name} onChange={set('name')} className={inp} />
            <input required type="email" placeholder="Work email *" value={form.work_email} onChange={set('work_email')} className={inp} />
            <input required placeholder="Company *" value={form.company} onChange={set('company')} className={inp} />
            <input placeholder="Phone (optional)" value={form.phone} onChange={set('phone')} className={inp} />
            <textarea placeholder="What are you looking for? (optional)" value={form.message} onChange={set('message')} className={`${inp} h-20 resize-none`} />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button type="submit" disabled={submitting || !form.name || !form.work_email || !form.company}
              className="w-full py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-60">
              {submitting ? 'Submitting…' : 'Request access'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
